import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {GraphService} from "../shared/services/graph.service";
import {VariablesPublicNamesService} from "../shared/services/variables-public-names.service";
import {GraphVariableModel} from "../shared/models/graph-variable.model";
import {CollectionModel} from "../shared/models/collection.model";
import {Subscription, TimeoutError} from "rxjs";
import {take, timeout} from "rxjs/operators";
import {BarChart, HeatmapChart, LineChart, PieChart} from "echarts/charts";
import * as echarts from 'echarts/core';
import {ScatterChart} from 'echarts/charts';
import {TitleComponent, TooltipComponent, GridComponent, LegendComponent, VisualMapComponent} from 'echarts/components';
import {CanvasRenderer} from 'echarts/renderers';
import {CookieService} from "ngx-cookie-service";
import {MatDialog} from "@angular/material/dialog";
import {DownloadCitationDialogComponent} from "../shared/components/download-citation-dialog/download-citation-dialog.component";
import {toSafeFileName} from "../shared/utils/safe-file-name";
import {resolveVariableDisplayName, VariablePublicNamesMap} from "../shared/utils/variable-display-name";
import {filterRequestableRows, GraphRequestContext} from "./graph-request-validity";
import {
  computeTimeSeriesChartLayout,
  createCanvasTextMeasurer,
  DEFAULT_CHART_GEOMETRY,
  DEFAULT_LEGEND_GEOMETRY,
  TextMeasurer,
  TimeSeriesChartLayout,
} from "./legend-layout";

echarts.use([ScatterChart, TitleComponent, TooltipComponent, GridComponent, CanvasRenderer, VisualMapComponent, HeatmapChart]);

@Component({
  selector: 'app-graph',
  templateUrl: './graph.component.html',
  styleUrls: ['./graph.component.scss'],
})
export class GraphComponent implements OnInit, OnDestroy {
  @Input() componentContext: 'graph-page' | 'dataset-page';
  @Input() collection?: CollectionModel;
  /**
   * #24 chart-enabled column keys for the active dataset. Supplied by Product Page Dataset
   * Charts only; `null` on the standalone `/graph` page, where no eligibility gate exists.
   */
  @Input() chartEnabledKeys: string[] | null = null;
  dataSubscription: Subscription;
  graphVariablesSubscription: Subscription;
  graphTypeSubscription: Subscription;
  graphDataTransformersSubscription: Subscription;
  graphDataTransformerInterpolateSubscription: Subscription;
  graphDataTransformerMultiAnnualMeanSubscription: Subscription;
  resetGraphSubscription: Subscription;
  selectedVariables: GraphVariableModel[];
  selectedGraphVariables: GraphVariableModel[];
  queryResults: any[];
  graphErrorMessage: string;
  profileCharts: any[];
  graphType: string;
  dataTransform: string;
  dataTransformInterpolate: boolean;
  dataTransformMultiAnnualMean: boolean;
  loading: boolean;
  xAxisCategoriesArray = [];
  yAxisCategoriesArray = [];
  valuesArray = [];
  months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  emptyResults: any[] = []
  publicNamesMap: VariablePublicNamesMap = {};

  /**
   * #29 — the Dataset Charts neutral state. An incomplete or stale selection is normal UI
   * state, not a system error, so this message is rendered plainly (never `text-danger`)
   * and only when no request has been issued.
   */
  static readonly NEUTRAL_MESSAGE = 'Select at least one variable and station to display the chart.';
  neutralMessage: string | null = null;

  /**
   * #29 — monotonic request identity. Incremented for every request issued *and* every
   * transition into the neutral state, so a response from a superseded or invalidated
   * request can be recognised and dropped rather than rendered.
   */
  private requestToken = 0;

  /**
   * Baseline bottom-grid reservation (px) for a time-series chart whose legend fits on one
   * row: the x-axis date-label band plus a single legend row. #28 made this the *floor*
   * rather than a fixed ceiling — `grid.bottom` is now `band + actual legend height`, so
   * additional wrapped rows push the plot up instead of drawing over the date axis
   * (G01/T21). The value is identical to the pre-#28 constant, which is what keeps a
   * one-row legend pixel-identical to the previous layout. See buildTimeSeriesChart().
   */
  static readonly LEGEND_GRID_BOTTOM_PX =
    DEFAULT_CHART_GEOMETRY.xAxisLabelBandPx + DEFAULT_LEGEND_GEOMETRY.rowHeightPx;

  /**
   * Canvas height for a one-row legend. Matches the height the echarts-for-angular
   * directive falls back to when the container has no CSS height, so small charts keep
   * exactly today's proportions.
   */
  static readonly BASE_CHART_HEIGHT_PX = DEFAULT_CHART_GEOMETRY.baseChartHeightPx;

  /**
   * Width assumed for the very first layout pass. `buildTimeSeriesChart()` runs before the
   * chart element exists, so there is nothing to measure yet; `onChartInit()` immediately
   * recomputes from the real container width and corrects this estimate.
   */
  static readonly FALLBACK_CHART_WIDTH_PX = 800;

  /**
   * Width delta below which a container resize is ignored. #28 grows the chart's *height*,
   * which re-triggers the container observer; without this guard the layout would feed
   * itself and oscillate. Only genuine width changes may recompute the legend.
   */
  static readonly RESIZE_WIDTH_EPSILON_PX = 2;

  /** Current canvas height, bound to the chart container. Grows with the legend row count. */
  timeSeriesChartHeightPx: number = DEFAULT_CHART_GEOMETRY.baseChartHeightPx;

  /** Full `series.name`s currently in the legend — the source of truth for a recompute. */
  private legendSeriesNames: string[] = [];

  /** Full name -> displayed (possibly width-guarded) label, read by the legend formatter. */
  private legendDisplayByName: Record<string, string> = {};

  /** Container width the current layout was computed from; `null` before the first pass. */
  private lastLegendLayoutWidthPx: number | null = null;

  private chartResizeObserver: ResizeObserver | null = null;

  /** Lazily created canvas measurer — one per component, reused for every layout pass. */
  private legendTextMeasurer: TextMeasurer | null = null;

  /**
   * Time-axis padding applied in scatter mode (G04/T22). A scatter series draws only
   * point symbols; on a `time` (value) x-axis the earliest and latest data points sit
   * exactly on the grid edges, where the symbol is clipped by the coordinate system and
   * becomes effectively invisible — so a valid sparse point that is visible in line mode
   * (its connecting path spans the range) disappears when switched to scatter. A small
   * axis `boundaryGap` insets the extreme points so every symbol is drawn fully inside
   * the plot. Kept as a small percentage so it scales with any date range and never
   * hard-codes a parameter, station, date, or value. See buildTimeSeriesChart().
   */
  static readonly SCATTER_TIME_AXIS_BOUNDARY_GAP: [string, string] = ['2%', '2%'];

  readonly echartsExtensions: any[];
  echartsOptions: object = {};
  echartsInstance: any;

  constructor(
    private graphService: GraphService,
    private variablesPublicNamesService: VariablesPublicNamesService,
    private dialog: MatDialog
  ) {
    this.echartsExtensions = [BarChart, TooltipComponent, GridComponent, LegendComponent, PieChart, LineChart];
  }

  ngOnInit(): void {
    echarts.use([ScatterChart, TitleComponent, TooltipComponent, GridComponent, CanvasRenderer]);
    this.variablesPublicNamesService.getVariablesPublicNames()
      .pipe(take(1))
      .subscribe(map => {
        this.publicNamesMap = map ?? {};
        this.loadGraphOnPageLoad();
      });
    this.subscribeToGraphSubjectsChanges();
  }

  /**
   * Build graph from data in cookie
   * @private
   */
  private loadGraphOnPageLoad(): void {
    const dataTransformKey = this.graphService.getDataTransformGraphKey(this.componentContext);
    const dataTransform = this.graphService.getLocalStorageSelectedVariables(dataTransformKey);
    this.dataTransform = dataTransform.length > 0 ? dataTransform : '';

    const dataTransformInterpolateKey = this.graphService.getDataTransformInterpolateKey(this.componentContext);
    const dataTransformInterpolate = this.graphService.getLocalStorageSelectedVariables(dataTransformInterpolateKey);
    // getLocalStorageSelectedVariables() already JSON.parses the stored value; parsing again here
    // throws "Unexpected token" on reload. Use the already-parsed value directly.
    this.dataTransformInterpolate = dataTransformInterpolate.length > 0 ? dataTransformInterpolate : '';

    const dataTransformMultiAnnualMeanKey = this.graphService.getDataTransformMultiAnnualMeanKey(this.componentContext);
    const dataTransformMultiAnnualMean = this.graphService.getLocalStorageSelectedVariables(dataTransformMultiAnnualMeanKey);
    this.dataTransformMultiAnnualMean = dataTransformMultiAnnualMean.length > 0 ? dataTransformMultiAnnualMean : '';

    const graphTypeKey = this.graphService.getGraphTypeKey(this.componentContext);
    const graphType = this.graphService.getLocalStorageSelectedVariables(graphTypeKey);
    this.graphType = graphType.length > 0 ? graphType : 'line';

    const variablesKey = this.graphService.getSelectedVariablesKey(this.componentContext);
    this.selectedVariables = this.graphService.getLocalStorageSelectedVariables(variablesKey);

    this.sendGraphDataRequest();
  }

  private subscribeToGraphSubjectsChanges(): void {
    this.graphVariablesSubscription = this.graphService.graphVariablesSubject.subscribe(selectedVariables => {
      this.selectedVariables = selectedVariables;
      if (this.hasRequestableSelection()) {
        this.sendGraphDataRequest();
      } else if (this.isDatasetPage()) {
        // #29 — leaving a valid selection for an incomplete one is a return to neutral, not
        // a silent no-op: stale chart/error output is cleared and any request still in
        // flight is invalidated.
        this.enterNeutralState();
      } else {
        this.emptyResults = [];
      }
    })

    // #29 — the four handlers below previously read `this.selectedVariables.length`, which
    // throws when the form resets (`graphVariablesSubject.next(null)`). The predicate is
    // null-safe; on `/graph` it reduces to the same non-empty test as before.
    this.graphTypeSubscription = this.graphService.graphTypeSubject.subscribe(graphType => {
      this.graphType = graphType;
      if (this.hasRequestableSelection()) {
        this.sendGraphDataRequest();
      }
    })

    this.graphDataTransformersSubscription = this.graphService.graphDataTransformersSubject.subscribe(dataTransform => {
      this.dataTransform = dataTransform;
      if (this.hasRequestableSelection()) {
        this.sendGraphDataRequest();
      }
    })

    this.graphDataTransformerInterpolateSubscription = this.graphService.graphDataTransformerInterpolateSubject.subscribe(dataTransformInterpolate => {
      this.dataTransformInterpolate = Boolean(dataTransformInterpolate);
      if (this.hasRequestableSelection()) {
        this.sendGraphDataRequest();
      }
    })

    this.graphDataTransformerMultiAnnualMeanSubscription = this.graphService.graphDataTransformerMultiAnnualMeanSubject.subscribe(dataTransformMultiAnnualMean => {
      this.dataTransformMultiAnnualMean = Boolean(dataTransformMultiAnnualMean);
      if (this.hasRequestableSelection()) {
        this.sendGraphDataRequest();
      }
    })

    this.resetGraphSubscription = this.graphService.resetGraph.subscribe(resetGraph => {
      if (resetGraph && (this.selectedVariables?.length ?? 0) === 0) {
        this.echartsOptions = {};
        this.profileCharts = [];
      }
      this.graphErrorMessage = null;
    })
  }

  /**
   * #29 — every behaviour added by this issue is confined to the Product Page Dataset
   * Charts surface (PM correction 1). Standalone `/graph` keeps its previous
   * initialization and request behaviour.
   */
  private isDatasetPage(): boolean {
    return this.componentContext === 'dataset-page';
  }

  /**
   * The identity context handed to the validity predicate: the active dataset and its #24
   * chart-enabled variables. Only ever built for Dataset Charts — `/graph` never reaches
   * the predicate at all, see `requestableRows()`.
   */
  private datasetPageRequestContext(): GraphRequestContext {
    return {
      collectionTitle: this.collection?.title ?? null,
      chartEnabledKeys: this.chartEnabledKeys ?? null,
    };
  }

  /**
   * The rows this component may ask the graph API about.
   *
   * On Dataset Charts this is the validated subset: complete rows that belong to the
   * active dataset and whose variable is chart-enabled. On `/graph` it is the selection
   * verbatim — the only difference from the previous code being that a `null` selection
   * yields `[]` instead of throwing.
   */
  private requestableRows(): GraphVariableModel[] {
    const rows = Array.isArray(this.selectedVariables) ? this.selectedVariables : [];
    return this.isDatasetPage() ? filterRequestableRows(rows, this.datasetPageRequestContext()) : rows;
  }

  private hasRequestableSelection(): boolean {
    return this.requestableRows().length > 0;
  }

  /**
   * #29 — the Dataset Charts neutral state: no chart, no error, no request.
   *
   * PM correction 2: the in-flight request is invalidated *before* the view is cleared.
   * Unsubscribing cancels the XHR, and bumping the token means that even a response that
   * has already been dispatched cannot re-enter `renderGraph()` or set an error message.
   * The invariant is that once the selection stops being requestable, nothing from an
   * earlier request can leave the neutral state.
   */
  private enterNeutralState(): void {
    this.requestToken++;
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
      this.dataSubscription = null;
    }

    this.loading = false;
    this.echartsOptions = {};
    this.profileCharts = [];
    this.emptyResults = [];
    this.graphErrorMessage = null;
    this.queryResults = null;
    // Nothing was requested, so there is nothing to render, export or download. Normalising
    // `null` to `[]` also keeps the template's `selectedVariables` guard truthy.
    this.selectedVariables = [];
    this.neutralMessage = GraphComponent.NEUTRAL_MESSAGE;
  }

  /**
   * #29 — is `token` still the request this component is waiting for, for the dataset it
   * was issued against? A superseded request, an invalidated one, or one issued before a
   * dataset change must not touch the view.
   */
  private isCurrentRequest(token: number, requestedCollectionTitle: string | null): boolean {
    return token === this.requestToken && (this.collection?.title ?? null) === requestedCollectionTitle;
  }

  private sendGraphDataRequest(format: string = 'json'): void {

    if (format === 'json') {
      this.echartsOptions = null;
      this.profileCharts = null;
    }

    const requestableRows = this.requestableRows();

    // #29 — Dataset Charts: nothing requestable means neutral state and no HTTP call. This
    // is the gate that stops initialization and restored state from producing a premature
    // request (and therefore a premature "unexpected error").
    if (this.isDatasetPage() && requestableRows.length === 0) {
      this.enterNeutralState();
      return;
    }

    // Keep `selectedVariables` aligned with what is actually requested: `renderGraph()`,
    // the CSV export and the file name all pair `queryResults[i]` with
    // `selectedVariables[i]`. Dropping unrequestable rows here without realigning would
    // shift every series onto the wrong variable.
    if (requestableRows.length !== (this.selectedVariables?.length ?? 0)) {
      this.selectedVariables = requestableRows;
    }

    const selectedGraphVariables = [];

    requestableRows.forEach(item => {
      selectedGraphVariables.push({
        station: item.station,
        category: item.category,
        collectionName: item.collectionName,
        column: item.column,
        depthMode: item.depthMode,
        singleDepth: item.singleDepth,
        fromDepth: item.fromDepth,
        toDepth: item.toDepth,
        depthAggregation: this.graphType === 'profile' ? null : item.depthAggregation,
        timeSteps: item.timeSteps,
        dataType: item.dataType,
        fromDate: item.fromDate,
        toDate: item.toDate,
      });
    })

    this.selectedGraphVariables = selectedGraphVariables;

    if (this.selectedGraphVariables.length > 0) {
      const interpolate = this.graphType === 'profile' ? true : Boolean(this.dataTransformInterpolate)
      let graphApiRequest = {
        dataTransform: this.dataTransform,
        interpolate: interpolate,
        multiAnnualMean: this.dataTransformMultiAnnualMean && this.graphType !== 'profile' ? Boolean(this.dataTransformMultiAnnualMean) : false,
        profile: this.graphType === 'profile',
        data: this.selectedGraphVariables,
        format
      }

      if (format === 'json') {
        this.loading = true;
      }

      this.graphErrorMessage = null;
      this.emptyResults = [];
      // A request is being issued, so this is no longer the neutral state.
      this.neutralMessage = null;

      if (this.dataSubscription) {
        this.dataSubscription.unsubscribe();
      }

      // #29 — capture the identity of *this* request. Both callbacks below return early
      // unless it is still the current one for the still-current dataset.
      const token = ++this.requestToken;
      const requestedCollectionTitle = this.collection?.title ?? null;

      this.dataSubscription = this.graphService.getGraphData(graphApiRequest, format).pipe(
        timeout(120000)
      ).subscribe(queryResults => {
        if (!this.isCurrentRequest(token, requestedCollectionTitle)) {
          return;
        }
        this.queryResults = queryResults;

        if (format === 'json') {
          this.renderGraph();
        } else {
          this.loading = false;
        }
      }, error => {
        if (!this.isCurrentRequest(token, requestedCollectionTitle)) {
          return;
        }
        this.loading = false;
        if (error instanceof TimeoutError) {
          this.graphErrorMessage = 'The request timed out. Please select a smaller dataset or a shorter time range and try again.';
        } else if (error?.error?.errorCode === 429) {
          this.graphErrorMessage = `The requested dataset is too large For <br>
                Station: ${error.error.station} <br>
                Variable: ${error.error.column} <br>
                From Date: ${this.formatDateAsDayMonthYear(error.error.fromDate)} <br>
                To Date: ${this.formatDateAsDayMonthYear(error.error.toDate)} <br>
                Depth: ${error.error.depthMode === 'singleDepth' ? error.error.singleDepth : error.error.fromDepth + ' ' + error.error.toDepth} <br>
                Please select a smaller dataset or a shorter time period.`;
        } else {
          this.graphErrorMessage = error?.error?.message ?? 'An unexpected error occurred. Please try again.';
        }
      })
    }
  }

  private allDataIsEmpty(): boolean {
    let emptyDataArray = [];

    this.queryResults.map(item => {
      if (item.length === 0) {
        emptyDataArray.push(true);
      } else {
        emptyDataArray.push(false);
      }
    })
    return emptyDataArray.every(value => value === true);
  }

  private doesTwoDatesHasLessThen24HoursInBetween(fromDate, toDate): boolean {
    const date1 = new Date(fromDate); // Replace with your first date
    const date2 = new Date(toDate); // Replace with your second date
    const timeDiff = Math.abs(date2.getTime() - date1.getTime());
    const oneDayInMillis = 24 * 60 * 60 * 1000;
    return timeDiff <= oneDayInMillis;
  }

  private renderGraph(): void {
    // If nothing to render, just return.
    if (!this.queryResults || !this.selectedVariables || !this.selectedVariables.length) {
      this.loading = false;
      this.echartsOptions = {};
      this.profileCharts = [];
      return;
    }

    // Filter out empty arrays from the server response and track them as "empty".
    // This ensures we skip them in the chart-building logic below.
    this.emptyResults = [];
    const cleanedQueryResults = [];
    const cleanedVariables = [];

    this.queryResults.forEach((result, index) => {
      if (Array.isArray(result) && result.length > 0) {
        cleanedQueryResults.push(result);
        cleanedVariables.push(this.selectedVariables[index]);
      } else {
        // If result is empty, mark that variable as having no data
        this.emptyResults.push(this.selectedVariables[index]);
      }
    });

    // If everything is empty, clear any old charts and show no data.
    if (!cleanedQueryResults.length) {
      this.loading = false;
      this.echartsOptions = {};
      this.profileCharts = [];
      return;
    }

    // If the user requested a profile graph, build it accordingly
    if (this.graphType === 'profile') {
      this.profileCharts = this.buildProfileCharts(cleanedQueryResults, cleanedVariables);
      this.echartsOptions = {}; // profile graphs live in `profileCharts`
      this.loading = false;
      return;
    }

    // Otherwise, build line/scatter (or bar, etc.) ECharts config
    this.echartsOptions = this.buildTimeSeriesChart(cleanedQueryResults, cleanedVariables);
    this.profileCharts = []; // not used for time-series
    this.loading = false;
  }

  /**
   * Build profile (vertical) charts, each returned as an ECharts option object.
   * This method is a refactor of the older buildProfileGraphArchive approach.
   */
  private buildProfileCharts(
    queryResultsArr: any[][],
    variables: GraphVariableModel[],
  ): any[] {
    const charts: any[] = [];

    variables.forEach((variable, idx) => {
      const rawData = queryResultsArr[idx];

      // If there’s no data, skip (though we filtered earlier).
      if (!rawData || !rawData.length) {
        this.emptyResults.push(variable);
        return;
      }

      // 1) Determine if fromDate–toDate is < 24h
      const singleDayProfile = this.doesTwoDatesHasLessThen24HoursInBetween(
        variable.fromDate,
        variable.toDate
      );

      if (singleDayProfile) {
        // Build a line chart: X = variable value, Y = depth
        const profileData = rawData.map(obj => [obj[variable.column], obj.depth]);
        charts.push({
          title: {
            text: this.collection?.short_name
              ? `${this.collection.short_name}, ${this.formatColumnDisplay(variable.column)}`
              : this.formatColumnDisplay(variable.column),
            left: 'center',
            bottom: '0',
            textStyle: {fontSize: 16},
          },
          xAxis: {type: 'value'},
          yAxis: {type: 'value'},
          tooltip: {trigger: 'axis', show: true},
          series: [
            {
              data: profileData,
              type: 'line',
              smooth: true,
              symbol: 'circle',
            },
          ],
        });
      } else {
        // Build heatmap: X = time, Y = depth, color = value
        // Create categories for X (time) and Y (depth)
        // For monthly-mean or raw data, handle differently as needed

        // 2) Gather distinct x-axis categories (times or months) and y-axis categories (depths)
        const xCategorySet = new Set<string | number>();
        const yCategorySet = new Set<number>();

        // Transform data into [xIndex, yIndex, value]
        const heatmapData: [number, number, number | null][] = [];

        rawData.forEach(obj => {
          const value = obj[variable.column];
          let xVal: string | number;

          if (variable.dataType === 'raw') {
            // Possibly multiAnnualMean with month
            if (this.dataTransformMultiAnnualMean) {
              xVal = obj.month; // numeric 1–12
            } else {
              // Otherwise date in ISO
              xVal = obj.date;
            }
          } else {
            // For aggregated data (weekly, monthly, yearly), transform the date label
            xVal = this.transformDateLabel(obj.date, variable.timeSteps);
          }

          xCategorySet.add(xVal);
          yCategorySet.add(obj.depth);

          // We store the final numeric index after we build arrays
          // but first just store the raw xVal & yVal to be replaced later
          // We'll do the transform once we have arrays from the sets
          heatmapData.push([xVal as any, obj.depth, value]);
        });

        // Convert sets to arrays (sorted for consistent ordering)
        const xCategories = Array.from(xCategorySet);
        // If it was month indices, you might sort numerically. If actual dates, you can sort by date.
        if (this.dataTransformMultiAnnualMean) {
          xCategories.sort((a: any, b: any) => Number(a) - Number(b));
        } else {
          xCategories.sort((a: any, b: any) => new Date(a).getTime() - new Date(b).getTime());
        }

        const yCategories = Array.from(yCategorySet).sort((a: any, b: any) => a - b);

        // Next, map xVal, yVal to their index positions
        const indexedHeatmapData = heatmapData.map(entry => {
          const [xRaw, depthRaw, val] = entry;
          const xIndex = xCategories.indexOf(xRaw);
          const yIndex = yCategories.indexOf(depthRaw);
          return [xIndex, yIndex, val];
        });

        // For the visualMap range
        const numericValues = indexedHeatmapData
          .filter(d => d[2] != null)
          .map(d => d[2] as number);

        const minVal = Math.floor(Math.min(...numericValues));
        const maxVal = Math.ceil(Math.max(...numericValues));

        // Build ECharts option
        charts.push({
          title: {
            text: this.collection?.short_name
              ? `${this.collection.short_name}, ${this.formatColumnDisplay(variable.column)}`
              : this.formatColumnDisplay(variable.column),
            left: 'center',
            top: '0',
            textStyle: {fontSize: 16},
          },
          tooltip: {position: 'top'},
          animation: false,
          grid: {
            height: '50%',
            top: '10%',
          },
          xAxis: {
            type: 'category',
            data: this.getCategoryLabels(xCategories, variable),
            splitArea: {show: true},
          },
          yAxis: {
            type: 'category',
            data: yCategories,
            splitArea: {show: true},
            inverse: true,
          },
          visualMap: {
            min: minVal,
            max: maxVal,
            calculable: true,
            orient: 'horizontal',
            left: 'center',
            bottom: '15%',
          },
          series: [
            {
              name: this.formatColumnDisplay(variable.column),
              type: 'heatmap',
              data: indexedHeatmapData,
            },
          ],
        });
      }
    });

    return charts;
  }

  /**
   * Build line/scatter (or other time-based) chart.
   */
  private buildTimeSeriesChart(
    queryResultsArr: any[][],
    variables: GraphVariableModel[]
  ): any {
    const series = [];
    const legendData = [];
    const allDataPoints: any[][][] = [];

    variables.forEach((item, index) => {
      const rawData = queryResultsArr[index];
      if (!rawData || !rawData.length) {
        this.emptyResults.push(item);
        return;
      }

      const dataPoints = rawData.map((obj: any) => {
        const xVal = this.dataTransformMultiAnnualMean ? obj.month : obj.date;
        return [xVal, obj[item.column]];
      });

      allDataPoints.push(dataPoints);

      const seriesName = `${index} ${item.station} ${this.formatColumnDisplay(item.column)}`;
      legendData.push(seriesName);

      series.push({
        name: seriesName,
        column: item.column,
        type: this.graphType,
        data: dataPoints,
      });
    });

    if (!series.length) {
      return {};
    }

    const xFormat = this.dataTransformMultiAnnualMean ? 'multi-annual' : this.detectXAxisFormat(allDataPoints);

    const uniqueColumns = [...new Set(variables.map(v => this.formatColumnDisplay(v.column)))];
    const chartTitle = this.collection?.short_name
      ? `${this.collection.short_name}, ${uniqueColumns.join(', ')}`
      : uniqueColumns.join(', ');

    const isTimeAxis = !this.dataTransformMultiAnnualMean;
    const xAxis: any = {
      type: isTimeAxis ? 'time' : 'category',
      axisLabel: {
        formatter: (value: string) => {
          if (this.dataTransformMultiAnnualMean) {
            return this.getMonthTitleByIndex(value);
          }
          const date = new Date(value);
          if (isNaN(date.getTime())) return value;
          if (xFormat === 'year-only') {
            return String(date.getFullYear());
          }
          return date.toLocaleDateString('en-US', {month: 'short', year: 'numeric'});
        },
      },
    };
    // G04/T22: in scatter mode, inset the extreme points from the time-axis edges so their
    // symbols are not clipped away (line mode is unaffected — its path already spans the
    // range; the multi-annual `category` axis already insets points via its default gap).
    if (isTimeAxis && this.graphType === 'scatter') {
      xAxis.boundaryGap = GraphComponent.SCATTER_TIME_AXIS_BOUNDARY_GAP;
    }

    // #28 — wrap the legend instead of paging it. This first pass runs before the chart
    // element exists, so it measures against FALLBACK_CHART_WIDTH_PX; onChartInit() then
    // recomputes from the real container width. Applying the height here (rather than
    // waiting for init) means a re-render of an already-live chart — e.g. the user
    // dropping from many series to few — resizes immediately instead of a frame later.
    const widthPx = this.measureChartContainerWidthPx();
    const layout = this.computeLegendLayout(legendData, widthPx);
    this.applyLegendLayout(legendData, layout, widthPx);
    this.resizeChartInstanceHeight(layout.chartHeightPx);

    return {
      tooltip: {trigger: 'axis'},
      title: chartTitle ? {text: chartTitle, left: 'center', textStyle: {fontSize: 14}} : undefined,
      // `grid.bottom` reserves the x-axis date-label band *plus* the measured legend
      // block, so the legend can never grow into the date labels (G01/T21) however many
      // rows it wraps onto. `containLabel` keeps the date labels inside the plot area.
      // The canvas grows by the same amount the legend does, so the plot itself is not
      // progressively squeezed as rows are added.
      grid: {left: '3%', right: '4%', bottom: layout.gridBottomPx, top: chartTitle ? '8%' : '3%', containLabel: true},
      xAxis,
      yAxis: {type: 'value'},
      legend: this.buildLegendOptions(legendData, layout),
      series,
    };
  }

  /**
   * #28 — the ECharts `legend` options for a wrapped multi-line legend.
   *
   * `data` always carries the complete, untruncated `series.name` list: every entry is
   * present, selection identity is unchanged, and tooltips/series semantics are untouched.
   * Only the *rendered* text goes through `formatter`, and only when a single label is wide
   * enough to swallow a whole row.
   *
   * The geometry here must mirror DEFAULT_LEGEND_GEOMETRY — it is what makes ECharts wrap
   * at the same points the layout helper measured, and therefore what makes the reserved
   * height match the drawn legend.
   */
  private buildLegendOptions(legendData: string[], layout: TimeSeriesChartLayout): any {
    return {
      // No `type: 'scroll'` — a pager hides entries, which is the defect #28 removes.
      data: legendData,
      formatter: (name: string) => this.legendDisplayByName[name] ?? name,
      bottom: 0,
      left: 'center',
      width: layout.legendWidthPx,
      padding: 0,
      itemWidth: DEFAULT_LEGEND_GEOMETRY.iconWidthPx,
      itemHeight: DEFAULT_LEGEND_GEOMETRY.iconHeightPx,
      itemGap: DEFAULT_LEGEND_GEOMETRY.itemGapPx,
      textStyle: {
        fontSize: DEFAULT_LEGEND_GEOMETRY.fontSizePx,
        fontFamily: DEFAULT_LEGEND_GEOMETRY.fontFamily,
      },
    };
  }

  private computeLegendLayout(legendData: string[], widthPx: number): TimeSeriesChartLayout {
    if (!this.legendTextMeasurer) {
      this.legendTextMeasurer = createCanvasTextMeasurer(DEFAULT_LEGEND_GEOMETRY);
    }
    return computeTimeSeriesChartLayout(legendData, widthPx, this.legendTextMeasurer);
  }

  /**
   * Record a computed layout: the legend identity, the display mapping the formatter reads,
   * and the container width this layout is valid for. Must run before the options carrying
   * it are handed to ECharts, so the formatter resolves against the current mapping.
   */
  private applyLegendLayout(
    legendData: string[],
    layout: TimeSeriesChartLayout,
    widthPx: number,
  ): void {
    this.legendSeriesNames = legendData;
    this.legendDisplayByName = layout.displayByName;
    this.lastLegendLayoutWidthPx = widthPx;
    this.timeSeriesChartHeightPx = layout.chartHeightPx;
  }

  /**
   * The live chart container's width, or the fallback before the element exists.
   * `getDom()` is the container the echarts-for-angular directive initialised on.
   */
  private measureChartContainerWidthPx(): number {
    const dom = this.chartContainerElement();
    const width = dom?.clientWidth ?? 0;
    return width > 0 ? width : GraphComponent.FALLBACK_CHART_WIDTH_PX;
  }

  private chartContainerElement(): HTMLElement | null {
    const instance = this.echartsInstance;
    if (!instance || typeof instance.getDom !== 'function' || this.isChartInstanceDisposed()) {
      return null;
    }
    return instance.getDom() ?? null;
  }

  private isChartInstanceDisposed(): boolean {
    const instance = this.echartsInstance;
    return !instance || (typeof instance.isDisposed === 'function' && instance.isDisposed());
  }

  /**
   * Resize the canvas to `heightPx`. Only the height is passed: the width is left to
   * ECharts' own 'auto', so it keeps tracking the fluid container and this call cannot
   * freeze the chart at a stale width. Passing the height explicitly (rather than relying
   * on the bound container style) removes any dependency on when Angular flushes the
   * binding — the two agree either way, the binding just keeps the DOM box in step.
   */
  private resizeChartInstanceHeight(heightPx: number): void {
    const instance = this.echartsInstance;
    if (this.isChartInstanceDisposed() || typeof instance.resize !== 'function') {
      return;
    }
    instance.resize({height: heightPx});
  }

  /**
   * Transform raw date into various label formats for aggregated data.
   */
  private transformDateLabel(dateStr: string, timeSteps: string): string | number {
    const dateObj = new Date(dateStr);
    if (isNaN(dateObj.getTime())) {
      return dateStr; // fallback if invalid
    }

    switch (timeSteps) {
      case 'weekly':
        return this.transformIsoDateToWeeklyFormat(dateStr);
      case 'monthly':
        return this.transformIsoDateToMonthlyFormat(dateStr);
      case 'yearly':
        return this.transformIsoDateToYearly(dateStr);
      default:
        return dateStr; // raw date
    }
  }

  /**
   * Utility to get final labels for xAxis if we need to convert month indices or date strings.
   */
  private getCategoryLabels(
    xCategories: (string | number)[],
    variable: GraphVariableModel
  ): string[] {
    // If the user used multiAnnualMean, xCategories might be an array of numeric months 1–12
    if (this.dataTransformMultiAnnualMean) {
      return xCategories.map(val => {
        const m = parseInt(String(val), 10);
        return (m >= 1 && m <= 12)
          ? this.months[m - 1]
          : String(val);
      });
    }

    // Otherwise, xCategories are date strings
    return xCategories.map(val => {
      const date = new Date(val);
      if (isNaN(date.getTime())) {
        return String(val);
      }
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    });
  }


  private getBaseChartOptions(): any {
    return {
      tooltip: {trigger: "axis"},
      grid: {left: "3%", right: "4%", bottom: "8%", top: "3%", containLabel: true},
      xAxis: {type: "category", data: []},
      yAxis: {type: "value"},
    };
  }

  private prepareSeriesData(queryResults, graphType, selectedVariables) {
    return selectedVariables.map((item, index) => {
      const data = queryResults.map(obj => {
        const x = graphType === 'profile' ? obj.date : obj.month;
        const y = obj[item.column];
        return [x, y];
      });
      return {
        name: `${item.station} ${item.column}`,
        type: graphType,
        data: data.filter(entry => entry[1] !== null), // Filter out null values
      };
    });
  }

  private buildProfileGraphArchive(): void {
    this.profileCharts = [];

    let mainSeries = [];

    this.yAxisCategoriesArray.sort((a, b) => {
      return a - b;
    });

    this.sortQueryResultsByDepth()

    this.selectedVariables.map((item, index) => {
      const xAxisArrayOfArraysCategories = [];
      xAxisArrayOfArraysCategories[index] = [];
      const yAxisArrayOfArraysCategories = [];
      yAxisArrayOfArraysCategories[index] = [];

      mainSeries[index] = [];

      let maxValue = -Infinity;
      let minValue = Infinity;

      if (this.queryResults[index].length > 0) {

      }

      this.queryResults[index].map((obj, i) => {
        let date = obj.date;
        let dateTimeFormatted;

        if (item.dataType === 'raw') {
          if (this.dataTransformMultiAnnualMean && this.graphType !== 'profile') {
            dateTimeFormatted = obj.month - 1;
            item.timeSteps = 'monthly';
          } else {
            dateTimeFormatted = obj.date;
          }
        } else {
          switch (item.timeSteps) {
            case 'weekly':
              dateTimeFormatted = this.transformIsoDateToWeeklyFormat(date);
              break;
            case 'monthly':
              dateTimeFormatted = this.transformIsoDateToMonthlyFormat(date);
              break;
            case 'yearly':
              dateTimeFormatted = this.transformIsoDateToYearly(date);
              break;
            default:
              dateTimeFormatted = date;
          }
        }

        let lessThenDayBetween = this.doesTwoDatesHasLessThen24HoursInBetween(item.fromDate, item.toDate);

        if (lessThenDayBetween) {
          // yAxis = depth
          // xAxis = value
          xAxisArrayOfArraysCategories[index].push(obj[item.column]);

          let dataArray = [
            obj.depth,
            obj[item.column],
          ];

          mainSeries[index].push(dataArray);

        } else {
          if (xAxisArrayOfArraysCategories[index].indexOf(dateTimeFormatted) === -1) {
            xAxisArrayOfArraysCategories[index].push(dateTimeFormatted)
          }

          if (yAxisArrayOfArraysCategories[index].indexOf(obj.depth) === -1) {
            yAxisArrayOfArraysCategories[index].push(obj.depth)
          }

          let dateIndexInxAxisCategories;
          let depthIndexInyAxisCategories;

          if (item.dataType === 'raw') {
            let xAxisColumn;
            if (this.dataTransformMultiAnnualMean && this.graphType !== 'profile') {
              xAxisColumn = obj.month - 1;
            } else {
              xAxisColumn = obj.date;
            }

            dateIndexInxAxisCategories = this.xAxisCategoriesArray[index].indexOf(xAxisColumn)
            depthIndexInyAxisCategories = this.yAxisCategoriesArray[index].indexOf(obj.depth)
            // depthIndexInyAxisCategories = obj.depth
          }

          let dataArray = [
            dateIndexInxAxisCategories,
            depthIndexInyAxisCategories,
            obj[item.column]
          ];

          mainSeries[index].push(dataArray);
        }

        if (obj[item.column] !== null && obj[item.column] > maxValue) {
          maxValue = obj[item.column];
        }

        if (obj[item.column] !== null && obj[item.column] < minValue) {
          minValue = obj[item.column];
        }
      })

      let lessThenDayBetween = this.doesTwoDatesHasLessThen24HoursInBetween(item.fromDate, item.toDate);
      let echartsOptions;

      if (lessThenDayBetween) {
        // profile line chart for variable data for one day
        echartsOptions = {
          title: {
            text: item.column,
            left: 'center',
            bottom: '0',
            textStyle: {
              fontSize: 16,
            },
          },
          xAxis: {
            type: 'value',
          },
          yAxis: {
            type: 'value',
          },
          series: [{
            data: mainSeries[index],
            type: 'line',
            smooth: true,
          }],
          symbol: 'circle',
          tooltip: {
            trigger: "axis",
            show: true
          },
        };

      } else {
        let xAxisValues;
        if (item.dataType === 'raw') {
          if (this.dataTransformMultiAnnualMean && this.graphType !== 'profile') {
            xAxisValues = this.months
          } else {
            xAxisValues = this.xAxisCategoriesArray[index].map(item => {
              /*let date = new Date(item);
              let year = date.getFullYear();
              let month = (date.getMonth() + 1).toString().padStart(2, '0');
              let day = date.getDate().toString().padStart(2, '0');
              return `${day}-${month}-${year}`;*/
              return this.formatDateAsDayMonthYear(item)
            });
          }
        }

        // sort array by depth ascending
        mainSeries[index].sort((a, b) => b[0] - a[0]);

        const graphDataValues = mainSeries[index].map(subarray => subarray[2]);

        echartsOptions = {
          title: {
            text: item.column,
            left: 'center',
            top: '0',
            textStyle: {
              fontSize: 16,
            },
          },
          timeSteps: item.timeSteps,
          tooltip: {
            position: 'top'
          },
          animation: false,
          grid: {
            height: '50%',
            top: '10%'
          },
          xAxis: {
            type: 'category',
            data: xAxisValues,
            splitArea: {
              show: true
            }
          },
          yAxis: {
            type: 'category',
            data: yAxisArrayOfArraysCategories[index],
            splitArea: {
              show: true
            },
            inverse: true
          },
          visualMap: {
            min: Math.floor(Math.min(...graphDataValues)),
            max: Math.ceil(Math.max(...graphDataValues)),
            calculable: true,
            orient: 'horizontal',
            left: 'center',
            bottom: '15%',
            /*inRange: {
              color: ['#e0ffff', '#006edd']
            }*/
          },
          series: [
            {
              name: item.column,
              type: lessThenDayBetween ? 'line' : 'heatmap',
              data: mainSeries[index]
            }
          ]
        };
      }

      if (mainSeries[index].length > 0) {
        this.profileCharts.push(echartsOptions);
      } else {
        this.emptyResults.push(item)
      }
    })
  }

  private sortQueryResultsByDepth(): void {
    this.queryResults.sort((a, b) => b.depth - a.depth);
  }

  private buildLineOrScatterGraph(legendData, series): void {
    series.map(dataObject => {
      if (dataObject.data.length === 0) {
        this.emptyResults.push(dataObject)
      }
    })

    if (this.emptyResults.length !== series.length) {
      this.echartsOptions = {
        tooltip: {
          trigger: "axis"
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "8%",
          top: "3%",
          containLabel: true
        },
        xAxis: {
          // type: 'time', // this.dataTransformMultiAnnualMean ? 'category' : 'time',
          type: this.dataTransformMultiAnnualMean ? 'category' : 'time',
          axisLabel: {
            formatter: (value) => {
              // build x-axis data as time
              // Convert the ISO date string to a JavaScript Date object
              let date = new Date(value);

              // Format the date to 'YYYY-MM-DD'
              let year = date.getFullYear();
              let month = (date.getMonth() + 1).toString().padStart(2, '0');
              let day = date.getDate().toString().padStart(2, '0');

              if (this.dataTransformMultiAnnualMean && this.graphType !== 'profile') {
                return this.getMonthTitleByIndex(value);
              } else {
                return `${day}-${month}-${year}`;
              }
            }
          },
        },
        yAxis: {
          type: "value"
        },
        legend: {
          data: legendData,
          bottom: 0
        },
        series: series
      }
    }
  }

  private transformIsoDateToWeeklyFormat(isoDate): string {
    const inputDate = new Date(isoDate);
    const dayOfWeek = inputDate.getDay(); // 0 for Sunday, 1 for Monday, ..., 6 for Saturday

    // Calculate the week start date (assuming Sunday as the start of the week)
    inputDate.setDate(inputDate.getDate() - dayOfWeek); // Move to the start of the week

    // Calculate the week number and year
    const weekNumber = Math.ceil((inputDate.getDate() - 1) / 7) + 1;
    const year = inputDate.getFullYear();

    return `Week ${weekNumber}, ${year}`;
  }

  private transformIsoDateToMonthlyFormat(isoDate: string): string {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }

    const month = this.months[date.getMonth()];
    const year = date.getFullYear();

    return `${month} ${year}`;
  }

  private getMonthTitleByIndex(monthIndex: string): string {
    const monthIndexNumber = parseInt(monthIndex)
    if (monthIndexNumber) {
      return this.months[monthIndexNumber - 1]
    }
  }

  private transformNumberToMonthly(monthNumber: number): string {
    return `${this.months[monthNumber - 1]}`;
  }

  private transformIsoDateToYearly(isoDate: string): string {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }

    const year = date.getFullYear();

    return year.toString();
  }

  /**
   * This function converts the graph JSON results to csv
   * It generates a csv file for each station-variable array
   * For each station-variable it defines the columns of the csv by the keys of the first array object
   */
  onDownloadArchive(): void {
    this.selectedVariables.map((item, index) => {
      const stationVariableArray = this.queryResults[index];
      if (stationVariableArray.length > 0) {
        let csvColumns = Object.keys(stationVariableArray[0]);
        const csvString = [
          csvColumns, // Define the Columns of csv the file - the keys of the first object in stationVariable array
          ...stationVariableArray.map(row => { // for each row return an array which consists of row values
            let rowObjectKeysToArray = [];

            Object.keys(row).forEach(key => {
              rowObjectKeysToArray.push(row[key])
            })

            return rowObjectKeysToArray;
          })
        ]
          .map(e => e.join(","))
          .join("\n");

        const blob = new Blob([csvString], {type: 'text/csv'});
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;

        let depth = item.depthMode === 'singleDepth' ? `Depth_${item.singleDepth}` : `FromDepth_${item.fromDepth}-ToDepth_${item.toDepth}`;

        let dateRage = `${this.formatDateAsDayMonthYear(item.fromDate)}-${this.formatDateAsDayMonthYear(item.toDate)}`;

        a.download = `Station_${item.station}-${item.column}-${depth}_${dateRage}.csv`;

        a.click();
        window.URL.revokeObjectURL(url);
      }
    })
  }


  onDownload(): void {
    this.openCitationModalThen(() => this.executeDownloadCsv());
  }

  private executeDownloadCsv(): void {
    const allVariableNames: Set<string> = new Set();
    const rawRows: any[] = [];

    this.selectedVariables.forEach((item, index) => {
      const stationVariableArray = this.queryResults[index];
      const variableName = item.column;
      allVariableNames.add(variableName);

      stationVariableArray.forEach(row => {
        const dateObj = new Date(row.date);
        const depth = row.depth;
        const station = item.station;

        const key = `${dateObj.toISOString()}|${depth}|${station}`;
        let existing = rawRows.find(r => r.__key === key);
        if (!existing) {
          existing = {
            __key: key,
            __dateObj: dateObj,
            date: '', // to be filled after sorting
            depth,
            station,
          };
          rawRows.push(existing);
        }

        existing[variableName] = row[variableName];
      });
    });

    // Sort by actual date
    rawRows.sort((a, b) => {
      const dateA = a.__dateObj.getTime();
      const dateB = b.__dateObj.getTime();
      if (dateA !== dateB) return dateA - dateB;
      return a.depth - b.depth;
    });

    // Format date string after sorting
    rawRows.forEach(r => {
      const d = r.__dateObj;
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      const ss = String(d.getSeconds()).padStart(2, '0');
      r.date = `"${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}"`;

      delete r.__key;
      delete r.__dateObj;
    });

    const sortedVariableNames = Array.from(allVariableNames).sort();
    const csvColumns = ['date', 'depth', 'station', ...sortedVariableNames];

    // #23: the header previously ran every column through `charAt(0).toUpperCase()`, which
    // rewrote the configured public name (`NO2_umol_l` -> `No2_umol_l`). Variable columns now
    // carry the configured public name — or, when none is configured, the technical
    // identifier unchanged. The three fixed metadata columns keep the capitalisation they
    // have always had, so existing date/depth/station behaviour does not regress.
    const displayColumns = [
      'Date',
      'Depth',
      'Station',
      ...sortedVariableNames.map(name => resolveVariableDisplayName(name, this.publicNamesMap)),
    ];
    const dataRows = [
      displayColumns.join(','),
      ...rawRows.map(row => csvColumns.map(col => row[col] ?? '').join(','))
    ].join('\n');

    const citationHeader = this.collection?.citation_text
      ? `# Usage is subject to mandatory citation:\n# ${this.collection.citation_text}\n\n`
      : '';

    const csvString = citationHeader + dataRows;

    const blob = new Blob([csvString], {type: 'text/csv'});
    const fileName = `${toSafeFileName(this.getGraphName())}.csv`;
    this.triggerBlobDownload(blob, fileName);
  }


  /**
   * Chart-facing label for a variable: its configured public name when there is one, the
   * unchanged technical identifier when there is not (#23), plus the collection's unit
   * suffix, which is unaffected by this issue.
   *
   * The fallback used to be `charAt(0).toUpperCase() + slice(1)`, which rewrote the
   * caller's casing. Resolution now lives in one shared helper so the chart, the
   * templates and the CSV header cannot drift apart.
   */
  formatColumnDisplay(column: string): string {
    if (!column) return column;
    const displayName = resolveVariableDisplayName(column, this.publicNamesMap);
    const units = this.collection?.column_units?.[column];
    return units ? `${displayName} (${units})` : displayName;
  }

  private detectXAxisFormat(allDataPoints: any[][][]): 'year-only' | 'month-year' {
    const yearMonths = new Map<number, Set<number>>();
    for (const points of allDataPoints) {
      for (const [xVal] of points) {
        const date = new Date(xVal);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = date.getMonth();
          if (!yearMonths.has(year)) yearMonths.set(year, new Set());
          yearMonths.get(year).add(month);
        }
      }
    }
    for (const months of yearMonths.values()) {
      if (months.size > 1) return 'month-year';
    }
    return 'year-only';
  }

  onChartInit(ec: any): void {
    this.echartsInstance = ec;
    // #28 — the first layout pass ran before this element existed and had to assume
    // FALLBACK_CHART_WIDTH_PX. Now that the real width is measurable, recompute against it
    // and start tracking further container-width changes.
    this.observeChartContainerWidth();
    this.recomputeLegendLayout();
  }

  /**
   * #28 — recompute the wrapped legend when the chart *container* changes width.
   *
   * A `window.resize` listener is not sufficient: the container can change width with no
   * window resize at all (sidebar toggles, layout reflow, the dataset panel expanding).
   * The echarts-for-angular directive's own two-second width poll only calls `resize()`;
   * it never recomputes how many rows the legend needs.
   *
   * Height changes are deliberately ignored — #28 *drives* the height, so reacting to it
   * would feed the observer back into itself.
   */
  private observeChartContainerWidth(): void {
    this.disconnectChartResizeObserver();

    const dom = this.chartContainerElement();
    if (!dom || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.chartResizeObserver = new ResizeObserver(entries => {
      const observedWidth = entries?.[0]?.contentRect?.width ?? dom.clientWidth;
      if (!observedWidth) {
        return;
      }
      if (
        this.lastLegendLayoutWidthPx !== null &&
        Math.abs(observedWidth - this.lastLegendLayoutWidthPx) <= GraphComponent.RESIZE_WIDTH_EPSILON_PX
      ) {
        return;
      }
      this.recomputeLegendLayout(observedWidth);
    });

    this.chartResizeObserver.observe(dom);
  }

  private disconnectChartResizeObserver(): void {
    if (this.chartResizeObserver) {
      this.chartResizeObserver.disconnect();
      this.chartResizeObserver = null;
    }
  }

  /**
   * Re-wrap the legend for `widthPx` (defaulting to the live container width) and re-apply
   * the resulting geometry to the live chart.
   *
   * Strictly layout-only: it re-measures labels and moves pixels. It never touches series
   * data, never rebuilds the selection, and never issues a graph API request — resizing a
   * chart must not re-query the backend (#29's request gating stays untouched).
   */
  private recomputeLegendLayout(widthPx?: number): void {
    if (!this.legendSeriesNames.length || this.isChartInstanceDisposed()) {
      return;
    }

    const width = widthPx ?? this.measureChartContainerWidthPx();
    const layout = this.computeLegendLayout(this.legendSeriesNames, width);
    const legendOptions = this.buildLegendOptions(this.legendSeriesNames, layout);

    this.applyLegendLayout(this.legendSeriesNames, layout, width);

    // Keep the bound options object in step, so a later full re-render by the directive
    // (which calls setOption with these options) does not resurrect the stale geometry.
    const options: any = this.echartsOptions;
    if (options?.grid) {
      options.grid.bottom = layout.gridBottomPx;
    }
    if (options?.legend) {
      Object.assign(options.legend, legendOptions);
    }

    if (typeof this.echartsInstance.setOption === 'function') {
      // Merge, not replace: series, axes and legend selection state are all preserved.
      this.echartsInstance.setOption({grid: {bottom: layout.gridBottomPx}, legend: legendOptions}, false);
    }

    // Height last, so the canvas is only re-laid-out once the new geometry is in place.
    this.resizeChartInstanceHeight(layout.chartHeightPx);
  }

  onDownloadPng(): void {
    if (!this.echartsInstance) return;
    this.openCitationModalThen(() => this.executeDownloadPng());
  }

  private executeDownloadPng(): void {
    const dataURL = this.echartsInstance.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: '#fff',
    });

    const fileName = `${toSafeFileName(this.getGraphName())}.png`;
    const citation = this.collection?.citation_text;

    // Always redraw onto a canvas and download as a Blob. A detached anchor
    // with a large data: URL makes Chromium drop the download filename (the
    // file lands as a UUID with no extension); a Blob URL avoids that.
    const img = new Image();
    img.onload = () => {
      // pixelRatio:2 means the image is 2× the display size — scale text/padding accordingly
      const scale = 2;
      const fontSize = 11 * scale;
      const padding = 10 * scale;
      const lineHeight = fontSize * 1.6;

      let footerLines: string[] = [];
      let footerHeight = 0;
      if (citation) {
        const measureCanvas = document.createElement('canvas');
        const mCtx = measureCanvas.getContext('2d')!;
        mCtx.font = `${fontSize}px Arial`;
        footerLines = this.wrapText(mCtx, citation, img.width - padding * 2);
        footerHeight = footerLines.length * lineHeight + padding * 2;
      }

      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height + footerHeight;
      const ctx = canvas.getContext('2d')!;

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      if (citation) {
        ctx.fillStyle = '#555555';
        ctx.font = `${fontSize}px Arial`;
        footerLines.forEach((line, i) => {
          ctx.fillText(line, padding, img.height + padding + (i + 1) * lineHeight);
        });
      }

      canvas.toBlob(blob => {
        if (blob) this.triggerBlobDownload(blob, fileName);
      }, 'image/png');
    };
    img.src = dataURL;
  }

  private wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    const words = text.split(' ');
    const lines: string[] = [];
    let current = '';
    for (const word of words) {
      const test = current ? `${current} ${word}` : word;
      if (ctx.measureText(test).width > maxWidth && current) {
        lines.push(current);
        current = word;
      } else {
        current = test;
      }
    }
    if (current) lines.push(current);
    return lines;
  }

  private triggerBlobDownload(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * The name of the *requested* chart, used to build download file names:
   * "<Short Name>, <Var>, <Var> - Stations <St>; <St>".
   *
   * Deliberately sourced from `selectedVariables` rather than the filtered
   * `cleanedVariables` used by the on-screen title (#33, D3): the file name
   * records what the user asked for, so a requested variable or station that
   * returned no data still appears. The rendered chart and the CSV contents show
   * which of those combinations actually produced data.
   *
   * Duplicates are removed in first-occurrence order; the user's ordering is
   * never re-sorted, which keeps the name deterministic for a given selection.
   */
  private getGraphName(): string {
    const columns = [...new Set(this.selectedVariables.map(v => this.formatColumnDisplay(v.column)))].join(', ');
    const base = this.collection?.short_name
      ? `${this.collection.short_name}, ${columns}`
      : columns;

    const stations = [...new Set(
      this.selectedVariables
        .map(v => v.station)
        .filter(station => station !== null && station !== undefined && `${station}`.trim() !== '')
    )];

    return stations.length ? `${base} - Stations ${stations.join('; ')}` : base;
  }

  private openCitationModalThen(action: () => void): void {
    if (!this.collection?.citation_text) {
      action();
      return;
    }
    const ref = this.dialog.open(DownloadCitationDialogComponent, {
      data: { citationText: this.collection.citation_text },
      width: '520px',
    });
    ref.afterClosed().subscribe(confirmed => {
      if (confirmed) action();
    });
  }

  formatDateAsDayMonthYear(date: string): string {
    let dateObject = new Date(date);
    let year = dateObject.getFullYear();
    let month = (dateObject.getMonth() + 1).toString().padStart(2, '0');
    let day = dateObject.getDate().toString().padStart(2, '0');
    return `${day}-${month}-${year}`;
  }

  formatDateAsDayMonthYearHorMinuteSeconds(date: string): string {
    const dateObj = new Date(date);
    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    const dd = String(dateObj.getDate()).padStart(2, '0');
    const hh = String(dateObj.getHours()).padStart(2, '0');
    const min = String(dateObj.getMinutes()).padStart(2, '0');
    const ss = String(dateObj.getSeconds()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
  }

  ngOnDestroy(): void {
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
    if (this.graphVariablesSubscription) {
      this.graphVariablesSubscription.unsubscribe();
    }
    if (this.graphTypeSubscription) {
      this.graphTypeSubscription.unsubscribe();
    }
    if (this.graphDataTransformersSubscription) {
      this.graphDataTransformersSubscription.unsubscribe();
    }
    if (this.graphDataTransformerInterpolateSubscription) {
      this.graphDataTransformerInterpolateSubscription.unsubscribe();
    }
    if (this.graphDataTransformerMultiAnnualMeanSubscription) {
      this.graphDataTransformerMultiAnnualMeanSubscription.unsubscribe();
    }
    if (this.resetGraphSubscription) {
      this.resetGraphSubscription.unsubscribe();
    }
    // #28 — the container observer outlives the component unless explicitly disconnected.
    this.disconnectChartResizeObserver();
  }

  protected readonly Object = Object;
}
